import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { usePartner } from "@/context/PartnerContext";
import { useApp } from "@/context/AppContext";
import { computeMonthly } from "@/lib/lease";
import {
  Users, Clock, Package, Star, PlugZap, LogIn, CreditCard, KeyRound, Bell,
  BarChart3, ShieldCheck, BookOpen, ExternalLink, Quote, Trophy, Award,
  Sparkles, ArrowRight, X, Headphones,
} from "lucide-react";

// Icon mapping, used by backend-configured stats/services
const ICONS = {
  Users, Clock, Package, Star, PlugZap, LogIn, CreditCard, KeyRound, Bell,
  BarChart3, ShieldCheck, Sparkles, Award, Headphones,
};
const IconBy = ({ name, className = "w-5 h-5" }) => {
  const C = ICONS[name] || Sparkles;
  return <C className={className} />;
};

/**
 * Partner profile page, shown inside a partner whitelabel route at
 * /p/:slug/over. Renders everything the admin configured on the Partner
 * document: badge, hero image, description, stats grid, services, testimonial,
 * featured case/content block and top-3 hardware the partner is combined with.
 */
export default function PartnerProfilePage({ sourceType = "partner" }) {
  const { slug } = useParams();
  const { partner: ctxPartner, prefix } = usePartner();
  const { formatPrice, leaseSettings } = useApp();
  const nav = useNavigate();

  // Partner or vendor (both expose the same profile field shape by design).
  const endpoint = sourceType === "vendor" ? `/vendors/${slug}` : `/partners/${slug}`;
  const [partner, setPartner] = useState(
    sourceType === "partner" && ctxPartner && ctxPartner.slug === slug ? ctxPartner : null
  );
  const [hardware, setHardware] = useState([]);
  const [loading, setLoading] = useState(!partner);

  useEffect(() => {
    setLoading(true);
    api.get(endpoint).then(({ data }) => setPartner(data)).finally(() => setLoading(false));
  }, [slug, endpoint]);

  useEffect(() => {
    api.get("/hardware").then(({ data }) => setHardware(data)).catch(() => {});
  }, []);

  const topHw = useMemo(() => {
    if (!partner?.top_hardware_skus?.length || !hardware.length) return [];
    return partner.top_hardware_skus
      .map((sku) => hardware.find((h) => h.sku === sku))
      .filter(Boolean);
  }, [partner, hardware]);

  const primary = partner?.primary_color || "#12B76A";
  const accent = partner?.accent_color || "#FF8D40";

  if (loading) {
    return <div className="container-x py-24 text-[#A8B3AD]" data-testid="partner-profile-loading">Profiel laden…</div>;
  }
  if (!partner) {
    return <div className="container-x py-24 text-[#A8B3AD]" data-testid="partner-profile-notfound">Partner niet gevonden.</div>;
  }

  return (
    <div className="pb-24" data-testid="partner-profile-page">
      {/* --- HERO --- */}
      <section className="relative">
        <div
          className="relative h-[320px] md:h-[420px] bg-cover bg-center"
          style={{
            backgroundImage: partner.hero_image_url ? `url(${partner.hero_image_url})` : undefined,
            backgroundColor: primary,
          }}
        >
          <div className="absolute inset-0" style={{
            background: `linear-gradient(180deg, rgba(0,0,0,.18) 0%, rgba(0,0,0,.65) 100%)`,
          }} />
          <button
            onClick={() => nav(`${prefix}/` || "/")}
            data-testid="partner-profile-close"
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 grid place-items-center text-white z-10 transition"
            aria-label="Sluiten"
          >
            <X className="w-5 h-5" />
          </button>

          {partner.badge && (
            <div className="absolute top-5 left-5 z-10">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide text-white"
                style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(6px)" }}
                data-testid="partner-profile-badge">
                <Award className="w-3.5 h-3.5" style={{ color: accent }} />
                {partner.badge}
              </span>
            </div>
          )}

          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 text-white z-10">
            <div className="max-w-5xl mx-auto flex flex-wrap items-end justify-between gap-6">
              <div>
                {partner.logo_url && (
                  <div className="mb-3 inline-flex items-center px-3 py-2 rounded-lg"
                    style={{ background: partner.logo_on_dark ? "transparent" : "rgba(255,255,255,0.92)" }}>
                    <img src={partner.logo_url} alt={partner.name} className="h-8 max-w-[180px] object-contain" />
                  </div>
                )}
                <h1 className="display force-white text-4xl md:text-5xl font-semibold leading-tight tracking-tight" data-testid="partner-profile-name">
                  {partner.name}
                </h1>
                {partner.tagline_nl && (
                  <p className="force-white mt-2 text-sm md:text-base opacity-90 max-w-xl">{partner.tagline_nl}</p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {partner.website_url && (
                  <a href={partner.website_url} target="_blank" rel="noreferrer" data-testid="partner-profile-website"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white transition hover:opacity-90"
                    style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(6px)" }}>
                    <ExternalLink className="w-4 h-4" /> Bezoek website
                  </a>
                )}
                {partner.blog_url && (
                  <a href={partner.blog_url} target="_blank" rel="noreferrer" data-testid="partner-profile-blog"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white border border-white/30 hover:bg-white/10 transition">
                    <BookOpen className="w-4 h-4" /> Lees blog
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-10 space-y-12">
        {/* --- STATS --- */}
        {!!partner.stats?.length && (
          <section>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="partner-profile-stats">
              {partner.stats.map((s, i) => (
                <div key={i} className="card-surface p-5 text-center" data-testid={`partner-stat-${i}`}>
                  <div className="w-10 h-10 mx-auto rounded-full grid place-items-center mb-2"
                    style={{ background: `${primary}22`, color: primary }}>
                    <IconBy name={s.icon} />
                  </div>
                  <div className="display text-2xl font-semibold">{s.value}</div>
                  <div className="text-[11px] uppercase tracking-[0.14em] text-[#A8B3AD] font-semibold mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* --- DESCRIPTION --- */}
        {partner.profile_description && (
          <section>
            <p className="text-[15px] leading-[1.7] text-[#F4F1EA] max-w-3xl" data-testid="partner-profile-description">
              {partner.profile_description}
            </p>
          </section>
        )}

        {/* --- SERVICES --- */}
        {!!partner.services?.length && (
          <section data-testid="partner-profile-services">
            <div className="flex items-baseline justify-between mb-5">
              <h2 className="display text-2xl md:text-3xl font-semibold">Wat levert {partner.name}?</h2>
              <span className="text-xs text-[#A8B3AD] uppercase tracking-[0.2em]">{partner.services.length} diensten</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {partner.services.map((s, i) => (
                <div key={i} className="card-surface p-5" data-testid={`partner-service-${i}`}>
                  <div className="w-10 h-10 rounded-lg grid place-items-center mb-3"
                    style={{ background: `${primary}18`, color: primary }}>
                    <IconBy name={s.icon} />
                  </div>
                  <div className="font-semibold text-[#F4F1EA]">{s.title}</div>
                  <div className="text-sm text-[#A8B3AD] mt-1.5 leading-relaxed">{s.description}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* --- FEATURED CONTENT (case study / podcast) --- */}
        {partner.featured_content?.title && (
          <section
            className="rounded-2xl p-6 md:p-8 text-white relative overflow-hidden"
            style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}CC 100%)` }}
            data-testid="partner-profile-featured"
          >
            <div className="flex items-start gap-5">
              <div className="shrink-0 w-14 h-14 rounded-xl grid place-items-center" style={{ background: "rgba(0,0,0,0.25)" }}>
                {partner.featured_content.kind === "podcast"
                  ? <Headphones className="w-6 h-6" />
                  : <Sparkles className="w-6 h-6" />}
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-90">
                  {partner.featured_content.overline || (partner.featured_content.kind === "podcast" ? "Podcast" : "Klantcase")}
                </div>
                <h3 className="display text-xl md:text-2xl font-semibold mt-1 leading-tight">
                  {partner.featured_content.title}
                </h3>
                {partner.featured_content.body && (
                  <p className="mt-2 text-sm opacity-95 leading-relaxed max-w-2xl">{partner.featured_content.body}</p>
                )}
                {partner.featured_content.cta_url && (
                  <a href={partner.featured_content.cta_url} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-full text-sm font-semibold bg-white/95 text-[#0F2A1E] hover:bg-white transition"
                    data-testid="partner-profile-featured-cta">
                    {partner.featured_content.cta_label || "Lees meer"} <ArrowRight className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          </section>
        )}

        {/* --- TESTIMONIAL --- */}
        {partner.testimonial?.quote && (
          <section className="card-surface p-6 md:p-8" data-testid="partner-profile-testimonial">
            <Quote className="w-8 h-8" style={{ color: primary }} />
            <p className="mt-3 display text-xl md:text-2xl leading-snug italic text-[#F4F1EA] max-w-3xl">
              &ldquo;{partner.testimonial.quote}&rdquo;
            </p>
            <div className="flex items-center gap-3 mt-6">
              <div className="w-11 h-11 rounded-full grid place-items-center text-[#0B110F] font-bold text-sm"
                style={{ background: primary }}>
                {partner.testimonial.initials || partner.testimonial.author?.slice(0, 1)?.toUpperCase()}
              </div>
              <div>
                <div className="font-semibold text-[#F4F1EA]">{partner.testimonial.author}</div>
                <div className="text-xs text-[#A8B3AD]">{partner.testimonial.role}</div>
              </div>
            </div>
          </section>
        )}

        {/* --- TOP HARDWARE --- */}
        {!!topHw.length && (
          <section data-testid="partner-profile-top-hardware">
            <div className="flex items-center gap-2 mb-5">
              <Trophy className="w-5 h-5" style={{ color: accent }} />
              <h2 className="display text-2xl md:text-3xl font-semibold">
                Top {topHw.length} meest gekozen bij {partner.name}
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {topHw.map((h, i) => (
                <Link key={h.id} to={`${prefix}/product/${h.slug || h.id}`}
                  className="card-interactive overflow-hidden group block"
                  data-testid={`partner-profile-top-hw-${i}`}>
                  <div className="relative">
                    <div className="absolute top-3 left-3 w-8 h-8 rounded-full grid place-items-center font-bold text-sm text-white z-10"
                      style={{ background: primary }}>
                      {i + 1}
                    </div>
                    <div className="product-plate h-56 grid place-items-center p-4">
                      {h.image_url && <img src={h.image_url} alt={h.name} className="max-h-full max-w-full object-contain" />}
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="font-semibold text-[#F4F1EA]">{h.name}</div>
                    <div className="text-xs text-[#A8B3AD] mt-0.5 capitalize">{h.placement}</div>
                    <div className="mt-3 flex items-baseline justify-between">
                      <div className="display text-lg font-semibold" style={{ color: primary }}>
                        {formatPrice(computeMonthly(h.purchase_price || 0, leaseSettings).monthly)}/mnd
                      </div>
                      <div className="text-xs text-[#A8B3AD]">of koop {formatPrice(h.purchase_price)}</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* --- CTA --- */}
        <section className="flex flex-col md:flex-row items-center justify-between gap-4 card-surface p-6 md:p-8"
          data-testid="partner-profile-cta">
          <div>
            <div className="display text-xl md:text-2xl font-semibold">Klaar om met {partner.name} te starten?</div>
            <div className="text-sm text-[#A8B3AD] mt-1">Stel je kiosk-setup samen in 5 minuten, wij nemen contact op binnen 1 werkdag.</div>
          </div>
          <Link to={`${prefix}/configurator`} data-testid="partner-profile-cta-start"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white transition hover:opacity-90"
            style={{ background: primary }}>
            Start configurator <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </div>
    </div>
  );
}
